import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { usePreferencesStore } from '../../store/preferencesStore';

interface CleanRaspiFilesDialogProps {
  open: boolean;
  onClose: () => void;
  raspiId: string;
}

interface CleanupStatus {
  status: 'IDLE' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  progress: number;
  filesDeleted: number;
  totalFiles: number;
  spaceFreed: number;
  errorMessage?: string;
}

export const CleanRaspiFilesDialog: React.FC<CleanRaspiFilesDialogProps> = ({
  open,
  onClose,
  raspiId
}) => {
  // Cleanup state
  const [cleanupStatus, setCleanupStatus] = useState<CleanupStatus>({
    status: 'IDLE',
    progress: 0,
    filesDeleted: 0,
    totalFiles: 0,
    spaceFreed: 0
  });
  
  const [isCleaning, setIsCleaning] = useState(false);
  const [targetFolder, setTargetFolder] = useState('/home/iop/IOP_DATA');

  useEffect(() => {
    if (open) {
      async function fetchTargetFolder() {
        try {
          const { STORE_PATH } = usePreferencesStore();
          setTargetFolder(STORE_PATH || '/home/iop/IOP_DATA');
        } catch {
          setTargetFolder('/home/iop/IOP_DATA');
        }
      }
      fetchTargetFolder();
    }
  }, [open]);

  const handleStartCleanup = async () => {
    setIsCleaning(true);
    setCleanupStatus({
      status: 'IN_PROGRESS',
      progress: 0,
      filesDeleted: 0,
      totalFiles: 150, // Example total
      spaceFreed: 0
    });

    try {
      // TODO: Implement actual API call
      // await cleanRaspiFiles(raspiId, targetFolder);
      
      // Simulate cleanup for now
      simulateCleanup();
      
    } catch (error) {
      console.error('Cleanup failed:', error);
      setCleanupStatus(prev => ({
        ...prev,
        status: 'FAILED',
        errorMessage: error instanceof Error ? error.message : 'Cleanup failed'
      }));
      setIsCleaning(false);
    }
  };

  const simulateCleanup = () => {
    // Simulate cleanup progress for demonstration
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 15;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        setCleanupStatus(prev => ({
          ...prev,
          status: 'COMPLETED',
          progress: 100,
          filesDeleted: 150,
          spaceFreed: 52428800 // 50MB
        }));
        setIsCleaning(false);
      } else {
        setCleanupStatus(prev => ({
          ...prev,
          progress: Math.round(progress),
          filesDeleted: Math.round((progress / 100) * 150),
          spaceFreed: Math.round((progress / 100) * 52428800)
        }));
      }
    }, 300);
  };

  const handleCancel = () => {
    if (isCleaning) {
      // TODO: Implement cancel cleanup
  
    }
    onClose();
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status: CleanupStatus['status']) => {
    switch (status) {
      case 'COMPLETED': return 'success.main';
      case 'FAILED': return 'error.main';
      case 'IN_PROGRESS': return 'info.main';
      default: return 'text.secondary';
    }
  };

  const getStatusText = (status: CleanupStatus['status']) => {
    switch (status) {
      case 'IDLE': return 'Ready to clean';
      case 'IN_PROGRESS': return 'Cleaning files...';
      case 'COMPLETED': return 'Cleanup completed successfully';
      case 'FAILED': return 'Cleanup failed';
      default: return 'Unknown status';
    }
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6">Clean RASPI Files</Typography>
          <Typography variant="subtitle2" color="text.secondary">
            ({raspiId})
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          
          {/* Warning Alert */}
          <Alert 
            severity="warning" 
            icon={<WarningIcon />}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              ⚠️ WARNING: This action will permanently delete all data!
            </Typography>
            <Typography variant="body2">
              All files in the <strong>{targetFolder}</strong> folder on RASPI device <strong>{raspiId}</strong> will be permanently deleted. This includes:
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              <Typography component="li" variant="body2">
                All experiment data and measurements
              </Typography>
              <Typography component="li" variant="body2">
                All captured images and camera data
              </Typography>
              <Typography component="li" variant="body2">
                All log files and system data
              </Typography>
              <Typography component="li" variant="body2">
                All temporary files and caches
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: 'error.main' }}>
              This action cannot be undone!
            </Typography>
          </Alert>

          <Divider />

          {/* Cleanup Information */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Cleanup Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Target Folder:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {targetFolder}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Estimated Files:
                </Typography>
                <Typography variant="body2">
                  ~150 files
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Estimated Space:
                </Typography>
                <Typography variant="body2">
                  ~50 MB
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Cleanup Progress */}
          {cleanupStatus.status !== 'IDLE' && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color={getStatusColor(cleanupStatus.status)}>
                  {getStatusText(cleanupStatus.status)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {cleanupStatus.progress}%
                </Typography>
              </Box>
              
              <LinearProgress 
                variant="determinate" 
                value={cleanupStatus.progress} 
                color={cleanupStatus.status === 'FAILED' ? 'error' : 'primary'}
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Files: {cleanupStatus.filesDeleted} / {cleanupStatus.totalFiles}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Space freed: {formatBytes(cleanupStatus.spaceFreed)}
                </Typography>
              </Box>
              
              {cleanupStatus.errorMessage && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {cleanupStatus.errorMessage}
                </Alert>
              )}
            </Box>
          )}

          {/* Confirmation */}
          {cleanupStatus.status === 'IDLE' && (
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'warning.light', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'warning.main'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Confirmation Required
              </Typography>
              <Typography variant="body2">
                To proceed with the cleanup, you must understand that this action will permanently delete all data from the RASPI device. 
                Make sure you have backed up any important data before continuing.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={handleCancel} 
          disabled={isCleaning}
          startIcon={<CancelIcon />}
        >
          {isCleaning ? 'Cancel' : 'Cancel'}
        </Button>
        <Button
          onClick={handleStartCleanup}
          disabled={isCleaning}
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
        >
          {isCleaning ? 'Cleaning...' : 'Clean All Files'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CleanRaspiFilesDialog; 